import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  FiberManualRecord as OnlineIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import useDebounce from '../../hooks/useDebounce';
import useAuthStore from '../../stores/authStore';
import { useSocket } from '../../contexts/SocketContext';

const OnlineUsers = ({ compact = false, maxHeight = '400px' }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSuperAdmin = useAuthStore(state => state.isSuperAdmin);
  const { socket, connected } = useSocket();

  const refreshOnlineUsers = () => {
    if (socket && connected && isSuperAdmin()) {
      socket.emit('get:online-users');
    }
  };

  useEffect(() => {
    // Only proceed if user is super admin and socket is connected
    if (!isSuperAdmin()) {
      setLoading(false);
      setError('Access restricted to super administrators');
      return;
    }

    if (!connected || !socket) {
      setLoading(true);
      setError(null);
      return;
    }

    // Request initial online users list
    socket.emit('get:online-users');

    // Listen for initial users list
    const handleOnlineUsersList = (data) => {
      setOnlineUsers(data.users || []);
      setLoading(false);
      setError(null);
    };

    // Listen for real-time user online/offline events
    const handleUserOnline = (data) => {
      setOnlineUsers(prev => {
        // Check if user already exists
        const existingIndex = prev.findIndex(user => user._id === data.user._id);
        if (existingIndex >= 0) {
          // Update existing user
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...data.user };
          return updated;
        } else {
          // Add new user
          return [...prev, data.user];
        }
      });
    };

    const handleUserOffline = (data) => {
      setOnlineUsers(prev => prev.filter(user => user._id !== data.userId));
    };

    const handleSocketError = (data) => {
      if (data.message) {
        setError(data.message);
        setLoading(false);
      }
    };

    // Set up event listeners
    socket.on('online-users:list', handleOnlineUsersList);
    socket.on('user:online', handleUserOnline);
    socket.on('user:offline', handleUserOffline);
    socket.on('error', handleSocketError);

    // Cleanup event listeners
    return () => {
      socket.off('online-users:list', handleOnlineUsersList);
      socket.off('user:online', handleUserOnline);
      socket.off('user:offline', handleUserOffline);
      socket.off('error', handleSocketError);
    };
  }, [isSuperAdmin, connected, socket]);

  const filteredUsers = onlineUsers.filter(user =>
    user.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  const getAvatarText = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getTimeSinceConnected = (connectedAt) => {
    if (!connectedAt) return '';
    
    const now = Date.now();
    const connected = new Date(connectedAt).getTime();
    const diffMs = now - connected;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  if (loading && onlineUsers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <OnlineIcon sx={{ color: 'success.main', fontSize: 14 }} />
            <Typography variant="h6">
              Online Users ({filteredUsers.length})
            </Typography>
          </Box>
        }
        action={
          <Box display="flex" alignItems="center" gap={1}>
            {!compact && (
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
            )}
            <IconButton 
              size="small" 
              onClick={refreshOnlineUsers}
              disabled={loading || !connected}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        }
      />
      <CardContent sx={{ p: compact ? 1 : 2 }}>
        {filteredUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            {searchTerm ? 'No users found matching your search' : 'No users online'}
          </Typography>
        ) : (
          <List dense={compact} sx={{ maxHeight, overflow: 'auto' }}>
            {filteredUsers.map((user) => (
              <ListItem key={user._id} alignItems="flex-start">
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <OnlineIcon 
                        sx={{ 
                          color: 'success.main', 
                          fontSize: 12,
                          backgroundColor: 'background.paper',
                          borderRadius: '50%'
                        }} 
                      />
                    }
                  >
                    <Avatar 
                      src={user.avatar}
                      alt={user.name}
                      sx={{ 
                        width: compact ? 32 : 40, 
                        height: compact ? 32 : 40,
                        bgcolor: 'primary.main'
                      }}
                    >
                      {getAvatarText(user.name)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {user.name}
                      </Typography>
                      {user.browserSessions > 1 && (
                        <Tooltip title={`Connected from ${user.browserSessions} browsers`}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <DevicesIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {user.browserSessions}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      {!compact && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {user.email}
                        </Typography>
                      )}
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {user.role && (
                          <Chip 
                            label={user.role} 
                            size="small" 
                            sx={{ mt: 0.5, height: 20 }}
                          />
                        )}
                        {user.connectedAt && (
                          <Typography 
                            variant="caption" 
                            color="success.main" 
                            sx={{ 
                              mt: 0.5, 
                              fontSize: '0.7rem',
                              fontWeight: 500 
                            }}
                          >
                            {getTimeSinceConnected(user.connectedAt)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default OnlineUsers;